import { createFileRoute } from "@tanstack/react-router"
import { GoogleVideoGenerationModelType } from "@aix/apiutils/src/image/enums"
import { AixContentBody, Separator, toast } from "@aix/ui"
import { ReactElement } from "react"
import CustomHead from "~/components/SEOHead/CustomHead"
import { VideoCatalog } from "~/components/Tools/MediaGeneration/VideoCatalog"
import { EmbeddedVideoGenerator } from "~/components/Tools/MediaGeneration/EmbeddedVideoGenerator"
import { MediaGenerationAlbumProvider } from "~/hooks/useAlbum"
import { useJobPolling } from "~/hooks/useJobPolling"
import AppLayout from "~/layouts/AppLayout"
import { api } from "~/utils/api"
import { getPageSEO } from "~/utils/seo"

function Veo3GeneratorPage() {
  const veo3SEO = getPageSEO("tools/veo3-generator")

  // Job polling hook with completion handler
  const { trackJob, activeJobCount, jobStatuses } = useJobPolling({
    onJobCompleted: () => {
      // Invalidate video catalog when job completes to show new videos
      void utils.assets.getAssetsGroupedByJobs.invalidate()
    },
  })

  // Get utils for invalidating queries
  const utils = api.useUtils()

  // Fetch available video models for the catalog
  const { data: modelData } = api.assets.getAvailableModels.useQuery()

  const handleVideoGeneration = (jobId: string) => {
    toast.success("Veo3 video generation started successfully!")

    // Start tracking the job
    trackJob(jobId)

    // Invalidate queries to refresh the catalog
    void utils.assets.getAssetsGroupedByJobs.invalidate()
  }

  const handleGenerationCancel = () => {
    toast.info("Video generation cancelled")
  }

  return (
    <>
      <CustomHead
        title={veo3SEO.title}
        description={veo3SEO.description}
        keywords={veo3SEO.keywords}
        pageType={veo3SEO.pageType}
        url="https://vidmax.ai/tools/veo3-generator"
      />

      <AixContentBody className="pt-16" innerClassName="gap-16">
        {/* Veo3 Generator Section */}
        <section className="flex flex-col gap-4">
          <EmbeddedVideoGenerator
            modelId={GoogleVideoGenerationModelType.VEO_3}
            onComplete={handleVideoGeneration}
            onCancel={handleGenerationCancel}
            description="Create stunning videos using Google's latest Veo3 model with optimized templates"
          />

          {activeJobCount > 0 && (
            <div className="p-4 mx-auto max-w-md rounded-lg border bg-primary/5 border-primary/20">
              <div className="flex gap-2 items-center text-primary">
                <div className="w-2 h-2 rounded-full animate-pulse bg-primary" />
                <span className="text-sm font-medium">
                  {activeJobCount} video{activeJobCount > 1 ? "s" : ""} generating...
                </span>
              </div>

              <p className="mt-1 text-xs text-muted-foreground">Your videos will appear below when ready</p>
            </div>
          )}
        </section>

        <Separator />

        {/* Generated Videos Section */}
        <section>
          <div className="flex flex-col justify-center items-center mb-6 w-full">
            <span className="text-sm font-semibold tracking-widest uppercase text-primary">Your Creations</span>
            <h2 className="text-2xl font-bold">Generated Videos</h2>
          </div>

          <VideoCatalog models={modelData?.models} jobStatuses={jobStatuses} />
        </section>
      </AixContentBody>
    </>
  )
}

function Veo3GeneratorPageWrapper() {
  return (
    <AppLayout>
      <MediaGenerationAlbumProvider>
        <Veo3GeneratorPage />
      </MediaGenerationAlbumProvider>
    </AppLayout>
  )
}

export const Route = createFileRoute("/tools/veo3-generator")({
  component: Veo3GeneratorPageWrapper,
})
